import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  MdArrowForward, 
  MdCheckCircle, 
  MdTimer, 
  MdVerified, 
  MdCalendarToday,
  MdTrendingUp,
  MdAutoAwesome,
  MdRocketLaunch,
  MdSecurity,
  MdThumbUp,
  MdStars,
  MdFlashOn
} from 'react-icons/md';
import { FaClock, FaGem, FaShieldAlt, FaChartLine, FaRocket, FaMagic, FaCrown, FaInfinity } from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiSpiralShell, GiCrystalBall } from 'react-icons/gi';
import './FinalCTASection.css';

const FinalCTASection = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  const buttonRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.9, 1, 1, 0.9]);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const guarantees = [
    { icon: <MdTimer />, text: "30 minutes chrono", color: "#3B82F6" },
    { icon: <MdVerified />, text: "Sans engagement", color: "#10B981" },
    { icon: <MdAutoAwesome />, text: "100% adapté à votre cas", color: "#8B5CF6" },
    { icon: <MdTrendingUp />, text: "Résultats concrets", color: "#F59E0B" }
  ];

  const benefits = [
    { text: "Diagnostic personnalisé", icon: <FaGem /> },
    { text: "Solutions sur mesure", icon: <FaMagic /> },
    { text: "ROI mesurable", icon: <FaChartLine /> },
    { text: "Accompagnement prioritaire", icon: <FaShieldAlt /> }
  ];

  const handleMouseMove = (e) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    }
  };

  const handleCalendlyClick = () => {
    window.open('https://calendly.com/votre-lien', '_blank');
    console.log('Ouverture du formulaire de réservation');
  };

  return (
    <section className="fcta-section-root" ref={sectionRef}>
      {/* Interactive Cursor Glow */}
      <motion.div 
        className="fcta-cursor-glow"
        animate={{
          x: mousePosition.x - 250,
          y: mousePosition.y - 250,
        }}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 150,
          mass: 0.8
        }}
      />

      {/* Animated Background Particles */}
      <div className="fcta-particles-container">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="fcta-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${20 + Math.random() * 25}s`,
              width: `${3 + Math.random() * 5}px`,
              height: `${3 + Math.random() * 5}px`,
              opacity: 0.2 + Math.random() * 0.5
            }}
          />
        ))}
      </div>

      {/* Animated Gradient Orbs */}
      <div className="fcta-animated-orbs">
        <div className="fcta-orb fcta-orb-1" style={{ transform: `translate(${scrollProgress * 150}px, ${scrollProgress * 80}px)` }} />
        <div className="fcta-orb fcta-orb-2" style={{ transform: `translate(-${scrollProgress * 120}px, -${scrollProgress * 90}px)` }} />
        <div className="fcta-orb fcta-orb-3" style={{ transform: `translate(${scrollProgress * 90}px, -${scrollProgress * 60}px)` }} />
        <div className="fcta-orb fcta-orb-4" style={{ transform: `translate(-${scrollProgress * 60}px, ${scrollProgress * 100}px)` }} />
      </div>

      {/* Floating Elements */}
      <div className="fcta-floating-elements">
        <GiGalaxy className="fcta-floating fcta-floating-1" />
        <GiSpiralShell className="fcta-floating fcta-floating-2" />
        <GiAstronautHelmet className="fcta-floating fcta-floating-3" />
        <GiStarsStack className="fcta-floating fcta-floating-4" />
        <GiCrystalBall className="fcta-floating fcta-floating-5" />
      </div>

      <motion.div 
        className="fcta-container"
        style={{ opacity, scale }}
      >
        {/* Main CTA Card */}
        <div className="fcta-main-card">
          {/* Card Glow Effect */}
          <div className="fcta-card-glow"></div>
          
          {/* Floating Stars */}
          <div className="fcta-stars">
            <div className="fcta-star fcta-star-1">✦</div>
            <div className="fcta-star fcta-star-2">✦</div>
            <div className="fcta-star fcta-star-3">✦</div>
            <div className="fcta-star fcta-star-4">✦</div>
            <div className="fcta-star fcta-star-5">✦</div>
          </div>

          {/* Rocket Animation */}
          <div className="fcta-rocket">
            <FaRocket />
            <div className="fcta-rocket-trail"></div>
          </div>

          {/* Content */}
          <div className="fcta-content">
            <motion.div 
              className="fcta-badge-wrapper"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="fcta-badge">
                <GiCrystalBall className="fcta-badge-icon" />
                <span>Offre limitée • Diagnostic gratuit</span>
                <MdStars className="fcta-badge-icon" />
              </span>
              <div className="fcta-badge-glow"></div>
            </motion.div>

            <motion.h2 
              className="fcta-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Prêt à arrêter de perdre
              <span className="fcta-highlight"> du temps et de l'argent ?</span>
            </motion.h2>

            <motion.p 
              className="fcta-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Réservez un diagnostic gratuit de 30 minutes. On analyse vos blocages ensemble 
              et on vous propose une solution concrète — sans engagement.
            </motion.p>

            {/* Benefits List */}
            <motion.div 
              className="fcta-benefits"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="fcta-benefit-item">
                  <div className="fcta-benefit-icon">
                    {benefit.icon}
                  </div>
                  <span>{benefit.text}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div 
              className="fcta-button-wrapper"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.button 
                ref={buttonRef}
                className="fcta-button"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleCalendlyClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="fcta-button-text">
                  Réserver mon diagnostic gratuit
                </span>
                <MdArrowForward className="fcta-button-icon" />
                
                {/* Hover Glow Effect */}
                <div 
                  className="fcta-button-glow"
                  style={{
                    background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.25), transparent)`
                  }}
                />
              </motion.button>
            </motion.div>

            {/* Guarantees */}
            <motion.div 
              className="fcta-guarantees"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {guarantees.map((guarantee, index) => (
                <div key={index} className="fcta-guarantee-item">
                  <div className="fcta-guarantee-icon" style={{ color: guarantee.color }}>
                    {guarantee.icon}
                  </div>
                  <span className="fcta-guarantee-text">{guarantee.text}</span>
                </div>
              ))}
            </motion.div>

            {/* Trust Indicators */}
            <motion.div 
              className="fcta-trust"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="fcta-trust-line"></div>
              <div className="fcta-trust-content">
                <MdSecurity className="fcta-trust-icon" />
                <span>Plus de 50 entreprises accompagnées</span>
                <MdThumbUp className="fcta-trust-icon" />
              </div>
              <div className="fcta-trust-line"></div>
            </motion.div>
          </div>

          {/* Decorative Elements */}
          <div className="fcta-decoration">
            <div className="fcta-circle fcta-circle-1"></div>
            <div className="fcta-circle fcta-circle-2"></div>
            <div className="fcta-circle fcta-circle-3"></div>
          </div>
        </div>

        {/* Small Print */}
        <motion.div 
          className="fcta-footer"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <p className="fcta-footer-text">
            💡 30 minutes peuvent tout changer — sans pression, juste des solutions concrètes pour votre business.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default FinalCTASection;